var FacetFilters = class extends HTMLElement {
  constructor() {
    super();
    this.filterGroups = {};
    this.detailsElements = null;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    
    // Single SVG with separate paths for animation
    this.toggleIcon = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 3.75V14.25" stroke="#101828" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M3.75 9H14.25" stroke="#101828" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  connectedCallback() {
    this.detailsElements = this.querySelectorAll('details');
    this.setupFilterGroups();
    this.initializeEventListeners();
    this.updateAllCounts();
    document.addEventListener('click', this.handleOutsideClick);

    window.addEventListener('DOMContentLoaded', () => {
      this.filterProducts();
      this.updateFilterCountsAndDisable(); 
    });

    // Add this resize handler
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1000) {
        document.querySelector('.filters__form-container')?.classList.remove('open', 'closed');
        document.querySelector('.filters__form-container')?.removeAttribute('inert');
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick(event) {
    const anyDetailsOpen = Array.from(this.detailsElements).some(details => details.open);
    if (!anyDetailsOpen) return;

    const isClickInsideDetails = event.target.closest('details');
    
    if (!isClickInsideDetails) {
      this.detailsElements.forEach(details => {
        if (details.open) {
          details.open = false;
        }
      });
    }
  }

  setupFilterGroups() {
    this.querySelectorAll(".filters__options").forEach((options) => {
      const groupName = options.dataset.filterGroup;
      this.filterGroups[groupName] = {
        options,
        checkboxes: options.querySelectorAll("input[type='checkbox']"),
        clearBtn: this.querySelector(`[data-clear-group="${groupName}"]`),
        countEl: this.querySelector(`#${groupName}-count`),
        details: options.closest("details"),
        summaryCountEl: options.closest("details").querySelector('.filters__selected-values-count'),
      };
    });
  }

  initializeEventListeners() {
    this._handleMobileFilters();
    this._handleClearAll();

    // Handle details toggle
    this.detailsElements.forEach((details) => {
      const summary = details.querySelector("summary");
      const icon = summary.querySelector(".toggle-icon");
      
      if (icon) icon.innerHTML = this.toggleIcon;

      summary.addEventListener("click", (e) => {
        e.preventDefault();

        // Close all detail elements except the current one
        this.detailsElements.forEach((otherDetails) => {
          if (otherDetails !== details) {
            otherDetails.open = false;
          }
        });

        // Toggle the current details
        details.open = !details.open;
      });
    });

    // Handle checkbox changes and clear buttons for each group
    Object.entries(this.filterGroups).forEach(([groupName, group]) => {
      group.checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", () => this.handleFilterChange(groupName));
      });

      group.clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearGroup(groupName);
      });
    });
  }

  _handleMobileFilters() {
    const mobileButton = this.querySelector('.filters__mobile-trigger');
    const formContainer = this.querySelector('.filters__form-container');
    const closeButton = this.querySelector('.filters__close-btn');
    const viewResultsButton = this.querySelector('.filters__view-results-btn');
    const htmlElement = document.documentElement;
    
    const closeFilters = () => {
      formContainer.classList.add('closing');
      formContainer.classList.remove('open');
      formContainer.classList.add('closed');
      mobileButton.setAttribute('aria-expanded', 'false');
      formContainer.setAttribute('inert', ''); // Use inert attribute instead of aria-hidden to handle focus traps on filter closing
      htmlElement.classList.remove('scroll-lock');

      // used to handle the closing animation and a zindex conflict with the overlay and the header
      setTimeout(() => {
        formContainer.classList.remove('closing');
      }, 500);
    };

    document.body.addEventListener('click', (e) => {
      if (e.target === document.body && htmlElement.classList.contains('scroll-lock')) {
        closeFilters();
      }
    });

    if (mobileButton && formContainer) {
      mobileButton.addEventListener('click', () => {
        const isOpen = formContainer.classList.contains('open');
        if (!isOpen) {
          formContainer.classList.remove('closed');
          formContainer.classList.add('open');
          mobileButton.setAttribute('aria-expanded', 'true');
          formContainer.removeAttribute('inert');
          htmlElement.classList.add('scroll-lock');
        } else {
          closeFilters();
        }
      });
    }

    if (closeButton && formContainer) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeFilters();
      });
    }

    if (viewResultsButton && formContainer) {
      viewResultsButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeFilters();
      });
    }
  }

  _handleClearAll() {
    const clearAllButton = this.querySelector('.filters__clear-all-btn');
    
    if (clearAllButton) {
      clearAllButton.addEventListener('click', (e) => {
        e.preventDefault();
        Object.keys(this.filterGroups).forEach(groupName => {
          this.clearGroup(groupName);
        });
      });
    }
  }

  handleFilterChange(groupName) {
    this.updateGroupCount(groupName);
    this.filterProducts();
    this.updateFilterCountsAndDisable();
  }

  clearGroup(groupName) {
    const group = this.filterGroups[groupName];

    // Uncheck all checkboxes in the group
    group.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Update the group count and recalculate states
    this.updateGroupCount(groupName);
    this.filterProducts();
    this.updateFilterCountsAndDisable();
  }

  updateGroupCount(groupName) {
    const group = this.filterGroups[groupName];
    const checkedCount = Array.from(group.checkboxes).filter((cb) => cb.checked).length;

    // Update the count in the filter summary
    if (group.summaryCountEl) {
      group.summaryCountEl.textContent = checkedCount > 0 ? `(${checkedCount})` : '';
    }

    // Update the individual count element
    group.countEl.textContent = checkedCount;

    // Show or hide the "Clear" button based on whether any checkboxes are selected
    if (checkedCount > 0) {
      group.clearBtn.style.display = "inline-block"; 
    } else {
      group.clearBtn.style.display = "none"; 
    }
  }

  updateAllCounts() {
    Object.keys(this.filterGroups).forEach((groupName) => {
      this.updateGroupCount(groupName);
    });
  }

  filterProducts() {
    const selectedFilters = this.getSelectedFilters();
    const prodCards = document.querySelectorAll(".prod-card");
    let visibleProductCount = 0;

    prodCards.forEach((card) => {
      const filterBlobAttr = card.dataset.filterBlob;

      if (!filterBlobAttr) {
        if (Object.values(selectedFilters).some((filters) => filters.length > 0)) {
          card.classList.add("prod-card--filtered");
        } else {
          card.classList.remove("prod-card--filtered");
          visibleProductCount++;
        }
        return;
      }

      let filterBlob;

      try {
        filterBlob = JSON.parse(filterBlobAttr);
      } catch (e) {
        console.error(
          `Error parsing data-filter-blob for card: ${card}`,
          e.message
        );
        if (Object.values(selectedFilters).some((filters) => filters.length > 0)) {
          card.classList.add("prod-card--filtered");
        } else {
          card.classList.remove("prod-card--filtered");
          visibleProductCount++;
        }
        return;
      }

      const isMatch =
        Object.values(selectedFilters).some((filters) => filters.length > 0) &&
        this.doesProductMatchFilters(filterBlob, selectedFilters);

      if (isMatch || !Object.values(selectedFilters).some((filters) => filters.length > 0)) {
        card.classList.remove("prod-card--filtered");
        visibleProductCount++;
      } else {
        card.classList.add("prod-card--filtered");
      }
    });

    const featuredSections = document.querySelectorAll(".featured-products");
    if (!featuredSections) return;
    featuredSections.forEach((section) => {
      const visibleCards = section.querySelectorAll(".prod-card:not(.prod-card--filtered)");
      const emptyMessage = section.querySelector(".featured-products__empty");

      if (!emptyMessage) return;
      if (visibleCards.length === 0) {
        emptyMessage.style.display = "block";
      } else {
        emptyMessage.style.display = "none";
      }
    });

    const filterCountElements = {
      desktop: document.querySelector("span.filters__total-products"),
      mobile: document.querySelector("button.filters__view-results-btn")
    };

    if (filterCountElements.desktop) filterCountElements.desktop.textContent = `${visibleProductCount} PRODUCTS`;
    if (filterCountElements.mobile) filterCountElements.mobile.textContent = `RESULTS (${visibleProductCount})`;
  }

  getSelectedFilters() {
    const selected = {};
    Object.entries(this.filterGroups).forEach(([groupName, group]) => {
      selected[groupName] = Array.from(group.checkboxes)
        .filter((cb) => cb.checked)
        .map((cb) => cb.value.toLowerCase());
    });
    return selected;
  }

  doesProductMatchFilters(filterBlob, selectedFilters) {
    return Object.entries(selectedFilters).every(([groupName, selectedValues]) => {
      if (selectedValues.length === 0) return true;

      const productValues = filterBlob[groupName]
        ?.split(",")
        .map((v) => v.trim().toLowerCase()) || [];
      return selectedValues.some((selectedValue) => productValues.includes(selectedValue));
    });
  }

  updateFilterCountsAndDisable() {
    const prodCards = document.querySelectorAll('.prod-card');

    Object.entries(this.filterGroups).forEach(([groupName, group]) => {
      group.checkboxes.forEach((checkbox) => {
        const filterValue = checkbox.value.toLowerCase();
        let matchCount = 0;

        prodCards.forEach((card) => {
          const filterBlobAttr = card.dataset.filterBlob;
          if (!filterBlobAttr) return;
          let filterBlob;

          try {
            filterBlob = JSON.parse(filterBlobAttr);
          } catch (e) {
            console.error(
              `Error parsing data-filter-blob for card: ${card}`,
              e.message
            );
            return;
          }

          const tempSelectedFilters = { ...this.getSelectedFilters() };
          tempSelectedFilters[groupName] = [filterValue];

          if (this.doesProductMatchFilters(filterBlob, tempSelectedFilters)) {
            matchCount++;
          }
        });

        checkbox.disabled = matchCount === 0;

        const countElement = checkbox.closest('.filters__label').querySelector('.filters__count');
        if (countElement) {
          countElement.textContent = `(${matchCount})`;
        }
      });
    });
  }
}
if (!window.customElements.get("facet-filters")) {
  customElements.define("facet-filters", FacetFilters);
}